<template>
  <div class="profile-badges">
    <inline-loader v-if="isLoading" />
    <template v-else>
      <!-- Main Badge Preview -->
      <div v-if="listOfBadges.length > 0" class="badge-main-preview">
        <div class="badge-image-wrapper">
          <DigitalBadge140 v-if="!hasCustomBadgeImage(currentBadgeSelected)" class="badge-image" />
          <img v-else :src="currentBadgeSelected.imageUrl" class="badge-image" />
        </div>
        <div class="badge-detail">
          <h6 class="title">{{ currentBadgeSelected.title }}</h6>
          <span class="awarded-date"> {{ awardedDate }} </span>
          <rich-text
            class="description"
            :content="formatDescription(currentBadgeSelected.description)"
            :expandable="false"
          />
          <link-button
            v-if="currentBadgeSelected.sourceType === DIGITAL_BADGE_SOURCE_TYPE.LESSON"
            class="view-more-button"
            size="small"
            kind="ghost"
            :disabled="isLoading"
            @click="handleViewMore"
          >
            {{ t("profile_subpage_digital_badges_view_more") }}
          </link-button>
        </div>
      </div>

      <!-- Badges content -->
      <div class="badges-content-wrapper">
        <!-- Badges earned header -->
        <div class="badges-earned">
          <svg-icon name="DigitalBadge24" kind="indicative" />
          <p>{{ t("profile_subpage_digital_badges_earned") }}</p>
          <span>{{ listOfBadges.length }}</span>
        </div>

        <div v-if="listOfBadges.length === 0" class="empty-profile-badge">
          <DigitalBadgeEmpty />
          <p>{{ t("profile_subpage_digital_badges_no_badges_text") }}</p>
        </div>

        <div v-else class="badge-list-wrapper">
          <!-- List of badges -->
          <div class="badge-list">
            <profile-badges-list
              :list-of-badges="listOfBadges"
              @selected-badge-index="handleSelectedBadge"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import ProfileBadgesList from "@modules/profile/components/ProfileBadgesList.vue";
import UserDigitalBadgeApi from "@apis/user.digital.badge.api";
import DigitalBadge140 from "@images/game/DigitalBadge140.svg?component";
import DigitalBadgeEmpty from "@images/placeholder/empty-states/DigitalBadgeEmpty.svg?component";
import DateTimeUtils from "@utils/datetime.utils";
import GameAwardPreviewContentSubpage from "@modules/attempt/components/gamification/GameAwardPreviewContentSubpage.vue";
import {
  DIGITAL_BADGE_GAME_AWARD_TYPE,
  DIGITAL_BADGE_SOURCE_TYPE,
} from "@modules/manage-digital-badge/service/digital.badge.constant";
import { ref, computed, onMounted } from "vue";
import { Badge } from "@modules/profile/profile-types.ts";
import { useI18n } from "vue-i18n";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";

interface Props {
  userId: number | string;
  description?: string;
}

const props = defineProps<Props>();

// Data
const isLoading = ref(true);
const listOfBadges = ref<Badge[]>([]);
const currentBadgeSelected = ref<Badge>({
  id: null,
  title: "",
  description: "",
  image: "",
  imageUrl: "",
  createdDate: "",
  sourceType: "",
});
const currentBadgeToViewMore = ref<Badge | null>(null);

const { t } = useI18n();
const { passiveApiFailHandler } = useErrorHandler();
const contentSubpageHandler = useContentSubpageInstance();

// Computed
const awardedDate = computed(() => {
  return `${t("profile_subpage_digital_badges_awarded_on")} ${displayDateTimeAsDateOnly(currentBadgeSelected.value.createdDate)}`;
});

// Methods
const fetchProfileBadges = async () => {
  try {
    // logic to fetch all the badges
    const response = await UserDigitalBadgeApi.getBadges({ userId: props.userId });

    if (response.code !== "ok") {
      isLoading.value = false;
      passiveApiFailHandler();
      return;
    }

    listOfBadges.value = response.data;

    // set currently selected preview as the first data
    if (listOfBadges.value.length > 0) {
      // set up badge title and description from JSON for all the badges in the list
      await setUpBadgesTitleAndDescription();

      // set up other fields (for the default currently selected option)
      currentBadgeSelected.value.id = listOfBadges.value[0].id; //UserDigitalBadge id
      currentBadgeSelected.value.title = listOfBadges.value[0].title;
      currentBadgeSelected.value.description = listOfBadges.value[0].description;
      currentBadgeSelected.value.image = listOfBadges.value[0].image;
      currentBadgeSelected.value.imageUrl = listOfBadges.value[0].imageUrl;
      currentBadgeSelected.value.createdDate = listOfBadges.value[0].createdDate;
      currentBadgeSelected.value.sourceType = listOfBadges.value[0].sourceType;
      currentBadgeSelected.value.digitalBadgeId = listOfBadges.value[0].digitalBadgeId; //DigitalBadge id
    }
  } catch (error) {
    console.error("Error fetching badges:", error);
    passiveApiFailHandler();
  }
};

const fetchProfileBadge = async () => {
  try {
    const response = await UserDigitalBadgeApi.getBadge(currentBadgeSelected.value.digitalBadgeId);

    if (response.code !== "ok") {
      passiveApiFailHandler();
      return;
    }

    // this can be null and it's intended
    // null would mean that the latest snapshot cannot be found for the badge
    const currentBadge = response.data;
    if (currentBadge != null) {
      await getBadgeJsonObject(currentBadge); // pass by reference
    }

    currentBadgeToViewMore.value = currentBadge;
  } catch (error) {
    console.error("Error fetching badge:", error);
    passiveApiFailHandler();
  }
};

const handleSelectedBadge = (badgeIndex) => {
  // update badge preview
  currentBadgeSelected.value.id = getCurrentBadgeObject(badgeIndex).id; // UserDigitalBadge id
  currentBadgeSelected.value.title = getCurrentBadgeObject(badgeIndex).title;
  currentBadgeSelected.value.description = getCurrentBadgeObject(badgeIndex).description
    ? getCurrentBadgeObject(badgeIndex).description
    : "";
  currentBadgeSelected.value.image = getCurrentBadgeObject(badgeIndex).image
    ? getCurrentBadgeObject(badgeIndex).image
    : null;
  currentBadgeSelected.value.imageUrl = getCurrentBadgeObject(badgeIndex).imageUrl
    ? getCurrentBadgeObject(badgeIndex).imageUrl
    : null;
  currentBadgeSelected.value.createdDate = getCurrentBadgeObject(badgeIndex).createdDate;
  currentBadgeSelected.value.sourceType = getCurrentBadgeObject(badgeIndex).sourceType;
  currentBadgeSelected.value.digitalBadgeId = getCurrentBadgeObject(badgeIndex).digitalBadgeId; // DigitalBadge id
};

const getCurrentBadgeObject = (badgeIndex) => {
  return listOfBadges.value[badgeIndex];
};

const hasCustomBadgeImage = (badge) => {
  return badge.image != null;
};

const displayDateTimeAsDateOnly = (dateArray) => {
  return DateTimeUtils.convertLocalDateTimeToDateOnly(dateArray);
};

const handleViewMore = async () => {
  await fetchProfileBadge();

  contentSubpageHandler.show(GameAwardPreviewContentSubpage, {
    baseTitle: [t("profile_subpage_digital_badges_back_to_profile_text")],
    hasParentSubpage: true,
    gameAward: currentBadgeToViewMore.value
      ? {
          title: currentBadgeToViewMore.value.title || currentBadgeSelected.value.title,
          description: currentBadgeToViewMore.value.description || "",
          featuredImageUrl: currentBadgeToViewMore.value.imageUrl || null,
          type: DIGITAL_BADGE_GAME_AWARD_TYPE.DIGITAL_BADGE,
          canBeFound: true,
          lessonUuid: currentBadgeToViewMore.value.lessonUuid || "",
          lessonTitle: currentBadgeToViewMore.value.lessonTitle || "",
          moeLibraryUuid: currentBadgeToViewMore.value.moeLibraryUuid || null,
        }
      : {
          title: currentBadgeSelected.value.title,
          canBeFound: false,
        },
    showModuleLink: true,
  });
};

const setUpBadgesTitleAndDescription = async () => {
  // get their title and description from the json object
  //Wait for all the promise to be back
  try {
    await Promise.all(listOfBadges.value.map((badge) => getBadgeJsonObject(badge)));
  } catch (error) {
    console.error("Error fetching or parsing JSON", error);
  }
};

const getBadgeJsonObject = async (badge): Promise<void> => {
  // badge contains snapshot of title and description]
  try {
    const badgeJsonData = await UserDigitalBadgeApi.getBadgeJsonObject(badge.jsonUrl);

    badge.title = badgeJsonData.title;
    badge.description = badgeJsonData.description;
  } catch (error) {
    console.error("Error fetching or parsing JSON", error);
    throw error;
  }
};

const sortBadgesByCreatedDate = () => {
  listOfBadges.value.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
};

const formatDescription = (description) => {
  if (description == null) return "";
  // this is necessary to not show media components in the preview
  return description.replace(/<div class="sls-media"[^>]*>.*?<\/div>/gs, "");
};

onMounted(async () => {
  await fetchProfileBadges();
  sortBadgesByCreatedDate();
  isLoading.value = false;
});
</script>

<style lang="scss">
@import "@styles/abstracts/functions";
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.profile-badges {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-06 $spacing-05;
  position: relative;

  @include media("desktop") {
    flex-wrap: nowrap;
  }

  .badge-main-preview {
    width: 100%;

    @include media("desktop") {
      max-width: 230px;
    }

    .badge-image-wrapper {
      margin-bottom: $spacing-05;
      text-align: center;

      img {
        max-width: 100%;
        height: auto;
      }
    }

    .badge-detail {
      width: 100%;
      padding: $spacing-05 $spacing-05 $spacing-04;
      background-color: $ui-06;
      border-radius: 10px;

      .title {
        @include carbon--type-style("productive-heading-01");
        @include line-clamp(2);
        color: $text-02;
        text-transform: uppercase;
      }

      .awarded-date {
        @include carbon--type-style("card-short-01");
        margin-bottom: $spacing-02;
        color: $text-02;
      }

      .description {
        @include line-clamp(4);
        margin-bottom: $spacing-02;
        font-weight: 400;
        border-radius: 0; // overwrite .rich-text border radius

        .rich-text-content {
          @include carbon--type-style("body-short-01");
        }
      }
    }
  }

  .badges-content-wrapper {
    flex: 1;
  }

  .badges-earned {
    display: flex;
    align-items: center;
    margin-bottom: $spacing-05;

    p {
      @include carbon--type-style("productive-heading-01");
      margin-left: $spacing-03;
      color: $text-02;
    }

    span {
      @include carbon--type-style("productive-heading-02");
      padding: $spacing-01 $spacing-03;
      margin-left: $spacing-04;
      background-color: tint($interactive-01, 90%);
      color: $interactive-01;
      border-radius: 5px;
    }
  }

  .empty-profile-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $spacing-05;
    margin: $spacing-06 0;

    p {
      @include carbon--type-style("productive-heading-01", true);
      max-width: 415px;
      color: $text-03;
      text-align: center;
    }
  }
}
</style>
